import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import { Locate } from 'lucide-react';
import 'maplibre-gl/dist/maplibre-gl.css';
import { point, lineString, featureCollection } from '@turf/helpers';
import { bbox, nearestPoint, distance, bearing } from '@turf/turf';
import type { Feature, LineString } from 'geojson';
import { useTranslation } from 'react-i18next';
import { useOfflineMap } from '../hooks/useOfflineMap';
import * as pmtilesIdb from '../services/pmtilesIdbService';
import { buildMapStyle } from '../utils/buildMapStyle';
import { DEFAULT_REGION_ID, DEFAULT_MAP_CENTER } from '../config/map';

const PMTILES_PATH = '/edc-mexico.pmtiles';
const PMTILES_MAGIC = new Uint8Array([0x50, 0x4d, 0x54, 0x69, 0x6c, 0x65, 0x73]); // "PMTiles"

function isPmtilesBuffer(buf: ArrayBuffer): boolean {
  if (buf.byteLength < 7) return false;
  const v = new Uint8Array(buf, 0, 7);
  for (let i = 0; i < 7; i++) if (v[i] !== PMTILES_MAGIC[i]) return false;
  return true;
}

/** Fetch PMTiles file and return a blob URL (works even when server omits Content-Length). */
async function fetchPmtilesAsBlobUrl(): Promise<string> {
  const res = await fetch(PMTILES_PATH);
  if (!res.ok) throw new Error(`PMTiles fetch failed: ${res.status}`);
  const buf = await res.arrayBuffer();
  if (!isPmtilesBuffer(buf)) throw new Error('Not a valid PMTiles file');
  const blob = new Blob([buf], { type: 'application/octet-stream' });
  return URL.createObjectURL(blob);
}

export interface MapLocation {
  lat: number;
  lng: number;
  label: string;
  currentDj?: string;
  currentTime?: string;
  nextDj?: string;
  nextTime?: string;
}

interface MapViewProps {
  locations: MapLocation[];
  highlightedStage?: string;
  onStageSelect?: (stage: string) => void;
  onReady?: () => void;
}

function createStageMarkerElement(
  loc: MapLocation,
  isHighlighted: boolean,
  markerTexts: {
    playingNow: string;
    nothingPlayingNow: string;
    upNext: (dj: string, nextTime?: string) => string;
  },
  onStageSelect?: (stage: string) => void
): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'relative cursor-pointer';
  wrapper.setAttribute('data-stage', loc.label);
  if (onStageSelect) {
    wrapper.addEventListener('click', () => onStageSelect(loc.label));
  }

  const el = document.createElement('div');
  el.className = `stage-marker px-2 py-1.5 rounded-lg text-left text-xs whitespace-nowrap transition-all ${
    isHighlighted
      ? 'bg-surface border-2 border-primary shadow-lg scale-105'
      : 'bg-surface/95 border border-gray-700'
  }`;

  const stageName = document.createElement('div');
  stageName.className = 'font-bold text-secondary uppercase tracking-wider text-[10px]';
  stageName.textContent = loc.label;
  el.appendChild(stageName);

  const currentLine = document.createElement('div');
  currentLine.className = 'mt-0.5 text-white';
  if (loc.currentDj) {
    currentLine.innerHTML = `<span class="text-gray-400 text-[9px]">${markerTexts.playingNow}</span><br/>${loc.currentDj}${loc.currentTime ? ` • ${loc.currentTime}` : ''}`;
  } else {
    currentLine.innerHTML = `<span class="text-gray-500 italic text-[9px]">${markerTexts.nothingPlayingNow}</span>`;
  }
  el.appendChild(currentLine);

  if (loc.nextDj) {
    const nextLine = document.createElement('div');
    nextLine.className = 'mt-0.5 pt-1 border-t border-gray-700 text-gray-400 text-[9px]';
    nextLine.textContent = markerTexts.upNext(loc.nextDj, loc.nextTime);
    el.appendChild(nextLine);
  }

  wrapper.appendChild(el);

  const tail = document.createElement('div');
  tail.style.cssText = `position:absolute;bottom:-10px;left:50%;transform:translateX(-50%);width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:10px solid ${isHighlighted ? '#BB86FC' : '#374151'};`;
  wrapper.appendChild(tail);

  return wrapper;
}

const DEFAULT_ZOOM = 16;

function bearingToCardinal(deg: number): string {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const idx = Math.round(((deg % 360) + 360) / 45) % 8;
  return dirs[idx];
}

export const MapView: React.FC<MapViewProps> = ({
  locations,
  highlightedStage,
  onStageSelect,
  onReady,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const userMarkerRef = useRef<maplibregl.Marker | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const blobUrlOwnedRef = useRef<boolean>(false);

  const { isProtocolReady } = useOfflineMap();

  const [status, setStatus] = useState<
    'loading' | 'downloading' | 'ready' | 'error' | 'offline'
  >('loading');
  const [userPosition, setUserPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const flyToLocation =
    highlightedStage && locations.length > 0
      ? locations.find((l) => l.label === highlightedStage) ?? locations[0]
      : null;

  const userPt = userPosition ? point([userPosition.lng, userPosition.lat]) : null;
  const locationPoints = locations.map((loc) => point([loc.lng, loc.lat], { label: loc.label }));
  const collection = locationPoints.length > 0 ? featureCollection(locationPoints) : null;
  const nearest = userPt && collection ? nearestPoint(userPt, collection) : null;
  const targetLoc =
    highlightedStage && locations.length > 0
      ? locations.find((l) => l.label === highlightedStage)
      : nearest
        ? locations[nearest.properties.featureIndex] ?? locations[0]
        : null;

  const distanceMeters =
    userPosition && targetLoc
      ? Math.round(distance([userPosition.lng, userPosition.lat], [targetLoc.lng, targetLoc.lat], { units: 'kilometers' }) * 1000)
      : null;
  const bearingDeg =
    userPosition && targetLoc
      ? bearing([userPosition.lng, userPosition.lat], [targetLoc.lng, targetLoc.lat])
      : null;
  const directionCardinal = bearingDeg != null ? bearingToCardinal(bearingDeg) : null;

  const handleRecenter = useCallback(() => {
    const map = mapRef.current;
    if (!map || !userPosition) return;
    map.flyTo({
      center: [userPosition.lng, userPosition.lat],
      zoom: DEFAULT_ZOOM,
      duration: 500,
    });
  }, [userPosition]);

  useEffect(() => {
    if (!isProtocolReady || !containerRef.current) return;

    let cancelled = false;
    const initialCenter: [number, number] =
      flyToLocation && locations.length > 0
        ? [flyToLocation.lng, flyToLocation.lat]
        : DEFAULT_MAP_CENTER;

    const init = async () => {
      try {
        const hasData = await pmtilesIdb.hasRegion(DEFAULT_REGION_ID);
        let pmtilesSourceUrl: string;

        if (hasData) {
          const blobUrl = await pmtilesIdb.getBlobUrl(DEFAULT_REGION_ID);
          if (cancelled) {
            pmtilesIdb.revokeBlobUrl(blobUrl);
            return;
          }
          blobUrlRef.current = blobUrl;
          blobUrlOwnedRef.current = false;
          pmtilesSourceUrl = blobUrl;
        } else {
          const blobUrl = await fetchPmtilesAsBlobUrl();
          if (cancelled) {
            URL.revokeObjectURL(blobUrl);
            return;
          }
          blobUrlRef.current = blobUrl;
          blobUrlOwnedRef.current = true;
          pmtilesSourceUrl = blobUrl;
        }

        const style = buildMapStyle(pmtilesSourceUrl);
        const [centerLng, centerLat] = initialCenter;

        const container = containerRef.current;
        if (!container) return;
        const map = new maplibregl.Map({
          container,
          style,
          center: [centerLng, centerLat],
          zoom: DEFAULT_ZOOM,
        });

        map.addControl(new maplibregl.NavigationControl(), 'top-right');

        // Provide placeholders for missing sprite images (e.g. "townhall") so the style
        // finishes loading and our route line and other layers can render.
        map.on('styleimagemissing', (e: { id: string }) => {
          const id = e.id;
          if (map.hasImage(id)) return;
          const size = 1;
          const data = new Uint8Array(size * size * 4);
          map.addImage(id, { width: size, height: size, data });
        });

        map.on('load', () => {
          if (cancelled) return;
          mapRef.current = map;
          setStatus('ready');
          onReady?.();
        });

        if (cancelled) {
          map.remove();
          if (blobUrlRef.current) {
            if (blobUrlOwnedRef.current) URL.revokeObjectURL(blobUrlRef.current);
            else pmtilesIdb.revokeBlobUrl(blobUrlRef.current);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Map init error:', err);
          setStatus('error');
        }
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [isProtocolReady]);

  useEffect(() => {
    if (status !== 'ready' || !mapRef.current) return;

    const map = mapRef.current;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    locations.forEach((loc) => {
      const isHighlighted = highlightedStage === loc.label;
      const el = createStageMarkerElement(
        loc,
        isHighlighted,
        {
          playingNow: t('map.playingNow'),
          nothingPlayingNow: t('map.nothingPlayingNow'),
          upNext: (dj: string, nextTime?: string) =>
            t('map.upNext', {
              dj,
              timePart: nextTime ? ` (${nextTime})` : '',
            }),
        },
        onStageSelect
      );
      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([loc.lng, loc.lat])
        .addTo(map);
      markersRef.current.push(marker);
    });
  }, [status, locations, highlightedStage, onStageSelect, t]);

  useEffect(() => {
    if (status !== 'ready') return;

    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {},
      { enableHighAccuracy: true }
    );
  }, [status]);

  useEffect(() => {
    const map = mapRef.current;
    if (status !== 'ready' || !map || !userPosition) return;

    const userEl = document.createElement('div');
    userEl.style.cssText =
      'width:12px;height:12px;border-radius:50%;background-color:#BB86FC;border:2px solid rgba(255,255,255,0.8);box-shadow:0 1px 3px rgba(0,0,0,0.3);';
    userMarkerRef.current?.remove();
    userMarkerRef.current = new maplibregl.Marker({ element: userEl })
      .setLngLat([userPosition.lng, userPosition.lat])
      .addTo(map);

    return () => {
      userMarkerRef.current?.remove();
      userMarkerRef.current = null;
    };
  }, [status, userPosition]);

  // Stable key so route effect doesn't run every second when only virtualTime (and thus locations ref) changes.
  const routeGeometryKey = useMemo(
    () =>
      locations.length === 0
        ? ''
        : JSON.stringify(
            locations.map((l) => ({ label: l.label, lng: l.lng, lat: l.lat }))
          ),
    [locations]
  );

  useEffect(() => {
    const map = mapRef.current;
    if (
      status !== 'ready' ||
      !map ||
      !userPosition ||
      locations.length === 0
    )
      return;

    const userPt = point([userPosition.lng, userPosition.lat]);
    const locationPoints = locations.map((loc) =>
      point([loc.lng, loc.lat], { label: loc.label })
    );
    const collection = featureCollection(locationPoints);
    const nearest = nearestPoint(userPt, collection);
    const targetLoc = highlightedStage
      ? locations.find((l) => l.label === highlightedStage)
      : locations[nearest.properties.featureIndex] ?? locations[0];
    if (!targetLoc) return;

    const line: Feature<LineString> = lineString(
      [
        [userPosition.lng, userPosition.lat],
        [targetLoc.lng, targetLoc.lat],
      ],
      { name: 'route' }
    );

    const sourceId = 'route-line';
    const layerId = 'route-line-layer';
    const routeData = featureCollection([line]);

    if (map.getSource(sourceId)) {
      (map.getSource(sourceId) as maplibregl.GeoJSONSource).setData(routeData);
    } else {
      map.addSource(sourceId, {
        type: 'geojson',
        data: routeData,
      });
      const addRouteLayer = () => {
        if (!map.getLayer(layerId)) {
          // Add on top of all style layers (no beforeId) so the line is visible
          map.addLayer(
            {
              id: layerId,
              type: 'line',
              source: sourceId,
              layout: {
                'line-join': 'round',
                'line-cap': 'round',
              },
              paint: {
                'line-color': '#BB86FC',
                'line-width': 3,
              },
            }
          );
        }
      };
      if (map.isStyleLoaded()) {
        addRouteLayer();
      } else {
        map.once('idle', addRouteLayer);
      }
    }
  }, [status, userPosition, highlightedStage, routeGeometryKey]);

  // Only run flyTo/fitBounds when status or highlightedStage changes, not when
  // locations reference changes (locations is recreated every second by virtualTime
  // in ScheduleContext, which was causing the map to recenter on every tick on mobile).
  useEffect(() => {
    const map = mapRef.current;
    if (status !== 'ready' || !map || locations.length === 0) return;

    if (highlightedStage) {
      const loc = locations.find((l) => l.label === highlightedStage);
      if (loc) {
        map.flyTo({
          center: [loc.lng, loc.lat],
          zoom: map.getZoom(),
          duration: 500,
        });
      }
    } else {
      const pts = locations.map((l) => point([l.lng, l.lat]));
      const fc = featureCollection(pts);
      const bounds = bbox(fc);
      const minSize = 0.001;
      const hasArea =
        bounds[2] - bounds[0] >= minSize || bounds[3] - bounds[1] >= minSize;
      if (hasArea) {
        map.fitBounds(
          [
            [bounds[0], bounds[1]],
            [bounds[2], bounds[3]],
          ],
          { padding: 60, duration: 500, maxZoom: 15 }
        );
      } else {
        map.flyTo({
          center: [bounds[0], bounds[1]],
          zoom: DEFAULT_ZOOM,
          duration: 500,
        });
      }
    }
  }, [status, highlightedStage]);

  useEffect(() => {
    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      userMarkerRef.current?.remove();
      userMarkerRef.current = null;
      mapRef.current?.remove();
      mapRef.current = null;
      if (blobUrlRef.current) {
        if (blobUrlOwnedRef.current) URL.revokeObjectURL(blobUrlRef.current);
        else pmtilesIdb.revokeBlobUrl(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  if (status === 'offline' || status === 'error') {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-surface text-white p-4">
        <p className="text-alert mb-2">
          {status === 'offline'
            ? t('map.mapDataNotDownloaded')
            : t('map.failedToLoadMap')}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary rounded-lg text-sm font-medium"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="w-full h-full min-h-[400px] relative">
      <div ref={containerRef} className="absolute inset-0 w-full h-full" />
      {status === 'loading' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-surface/95 text-white z-10">
          <p className="text-gray-400">{t('map.loadingMap')}</p>
        </div>
      )}
      {status === 'ready' && userPosition && (
        <button
          type="button"
          onClick={handleRecenter}
          aria-label={t('map.recenterOnLocation')}
          className="absolute top-2 right-12 z-10 p-2 rounded-lg bg-surface/95 border border-gray-700 text-white hover:bg-surface hover:border-primary transition-colors"
        >
          <Locate className="w-5 h-5" />
        </button>
      )}
      {status === 'ready' && targetLoc && (
        <div className="absolute top-2 left-4 z-20 px-3 py-2 rounded-lg bg-surface border border-gray-600 text-white text-sm shadow-lg">
          {userPosition && distanceMeters != null && directionCardinal ? (
            <>
              <span className="font-semibold">{distanceMeters} m</span>
              <span className="text-gray-400 mx-1.5">·</span>
              <span className="text-primary font-medium">{directionCardinal}</span>
            </>
          ) : (
            <span className="text-gray-400 text-xs">
              {t('map.enableLocationForDistance')}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
